/**
 * Bed-booking read API (M10-T03). Per bed type: clinical `free` and `bookable`
 * (= free − held), plus the hospital's indicative "beds from" rate. The unit is
 * the bed TYPE — specific beds aren't bookable (ADR-0019); the clinical team
 * assigns the actual bed at admission (ADR-0020).
 */
import { apiFetch } from '../lib/api'

export type BedTypeAvailability = {
  bed_type: string
  free: number
  bookable: number
}

export type HospitalBookable = {
  hospital_slug: string
  bed_rate_from_pesewas: number | null
  types: BedTypeAvailability[]
}

/** Human label for each bed-type slug. */
export const BED_TYPE_LABELS: Record<string, string> = {
  icu: 'ICU',
  general: 'General',
  private: 'Private',
  executive: 'Executive',
  maternity: 'Maternity',
}

export const bookableQueryKey = (slug: string) => ['bookable', slug] as const

/** GET /api/v1/hospitals/{slug}/bookable. Throws `ApiError` (404 unknown slug). */
export async function fetchBookable(slug: string): Promise<HospitalBookable> {
  const body = await apiFetch<{ data: HospitalBookable }>(
    `/api/v1/hospitals/${slug}/bookable`,
  )
  return body.data
}

export type BookingCreated = {
  reference: string
  status: string
  bed_type: string
  total_pesewas: number
  currency: string
  hold_expires_at: string
}

/** POST /api/v1/patient/bookings (auth) — secure a place by bed type (M10-T04).
 * Holds the place in `pending_payment` until paid. Throws `ApiError`
 * (`no_capacity` if the type just filled, 404 unknown hospital). */
export async function createBooking(
  token: string,
  hospitalSlug: string,
  bedType: string,
): Promise<BookingCreated> {
  const body = await apiFetch<{ data: BookingCreated }>(
    '/api/v1/patient/bookings',
    {
      method: 'POST',
      token,
      body: { hospital_slug: hospitalSlug, bed_type: bedType },
    },
  )
  return body.data
}

export type PaymentInitialized = {
  authorization_url: string
  reference: string
}

/** POST /api/v1/patient/payments/initialize (auth) — start the deposit on
 * Paystack's hosted page (M10-T05b). Returns the URL to redirect to. Idempotent
 * per booking. Throws `ApiError` (`payment_unavailable` 503, `hold_expired` 409). */
export async function initializeBookingPayment(
  token: string,
  bookingReference: string,
): Promise<PaymentInitialized> {
  const body = await apiFetch<{ data: PaymentInitialized }>(
    '/api/v1/patient/payments/initialize',
    { method: 'POST', token, body: { booking_reference: bookingReference } },
  )
  return body.data
}
